import React, { forwardRef, useCallback, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import { isFunction } from 'lodash';

import { THEME } from 'admin/components/common/Theme';
import { Errors } from 'admin/components/common/Form/Errors';
import { Hint } from 'admin/components/common/Form/Hint';
import { LabelProps, Label } from 'admin/components/common/Form/Label';
import type { ReduxFieldInputTypes, ReduxFieldMetaTypes } from 'admin/components/common/Form/utils';
import commonStyles from '../common.scss';
import css from './TextArea.scss';

type TextAreaElementProps = React.JSX.IntrinsicElements['textarea'];

type TextAreaCommonProps = Pick<
	TextAreaElementProps,
	'rows' | 'className' | 'readOnly' | 'placeholder' | 'disabled' | 'autoComplete' | 'autoFocus' | 'dir' | 'aria-label'
> & {
	theme?: ValuesType<typeof THEME>;
	resize?: 'vertical' | 'none';
	autoSize?: { minRows: number; maxRows: number };
	label?: Omit<LabelProps, 'component'>;
};

type TextAreaPureProps = TextAreaCommonProps &
	Pick<
		TextAreaElementProps,
		| 'value'
		| 'name'
		| 'id'
		| 'onChange'
		| 'onBlur'
		| 'onFocus'
		| 'onKeyDown'
		| 'onPaste'
		| 'onSelect'
		| 'aria-describedby'
		| 'aria-autocomplete'
		| 'aria-haspopup'
		| 'aria-controls'
		| 'aria-activedescendant'
	>;

export const TextAreaPure = forwardRef<HTMLTextAreaElement, TextAreaPureProps>(
	({ theme = 'light', rows = 5, readOnly = false, resize = 'none', autoSize, ...props }, forwardedRef) => {
		const textareaRef = useRef<HTMLTextAreaElement | null>(null);
		const minRows = autoSize?.minRows;
		const maxRows = autoSize?.maxRows;
		useImperativeHandle(forwardedRef, () => textareaRef.current as HTMLTextAreaElement);
		const resizeToContent = useCallback(
			(element: HTMLTextAreaElement) => {
				if (minRows === undefined || maxRows === undefined) return;
				const textarea = element;
				const style = window.getComputedStyle(textarea);
				const lineHeight = Number.parseFloat(style.lineHeight) || 20;
				const verticalPadding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
				const minHeight = lineHeight * minRows + verticalPadding;
				const maxHeight = lineHeight * maxRows + verticalPadding;

				textarea.style.height = 'auto';
				const height = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
				textarea.style.height = `${height}px`;
				textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
			},
			[maxRows, minRows]
		);

		useLayoutEffect(() => {
			const textarea = textareaRef.current;
			if (!textarea) return undefined;

			resizeToContent(textarea);
			if (minRows === undefined || !('ResizeObserver' in window)) return undefined;

			let { width } = textarea.getBoundingClientRect();
			const observer = new ResizeObserver(() => {
				const nextWidth = textarea.getBoundingClientRect().width;
				if (nextWidth === width) return;
				width = nextWidth;
				resizeToContent(textarea);
			});
			observer.observe(textarea);

			return () => observer.disconnect();
		}, [minRows, props.value, resizeToContent]);

		return (
			<label
				className={cn(css.textareaField, css[theme], css[`resize-${resize}`], props.className, {
					[css.readOnly]: readOnly,
					[commonStyles.disabled]: props.disabled,
				})}
			>
				{props.label ? (
					<Label
						theme={props.label.theme ?? theme}
						className={cn(css.labelText, props.label.className)}
						weight={props.label.weight}
						component="div"
					>
						{props.label.children}
					</Label>
				) : null}
				<div className={css.fieldWrapper}>
					<textarea
						value={props.value}
						aria-label={props['aria-label']}
						aria-describedby={props['aria-describedby']}
						aria-autocomplete={props['aria-autocomplete']}
						aria-haspopup={props['aria-haspopup']}
						aria-controls={props['aria-controls']}
						aria-activedescendant={props['aria-activedescendant']}
						dir={props.dir}
						id={props.id}
						rows={minRows ?? rows}
						name={props.name}
						placeholder={props.placeholder}
						autoComplete={props.autoComplete}
						onChange={
							props.disabled
								? undefined
								: event => {
										resizeToContent(event.currentTarget);
										props.onChange?.(event);
									}
						}
						onBlur={props.disabled ? undefined : props.onBlur}
						onFocus={props.disabled ? undefined : props.onFocus}
						onKeyDown={props.disabled ? undefined : props.onKeyDown}
						onPaste={props.disabled ? undefined : props.onPaste}
						onSelect={props.disabled ? undefined : props.onSelect}
						disabled={props.disabled}
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus={props.autoFocus}
						ref={textareaRef}
						readOnly={readOnly}
					/>
				</div>
			</label>
		);
	}
);

interface TextAreaProps extends TextAreaCommonProps {
	input: ReduxFieldInputTypes;
	meta: ReduxFieldMetaTypes;
	hint?: React.ReactNode;
	showErrorsOn?: keyof ReduxFieldMetaTypes | ((meta: ReduxFieldMetaTypes, input: ReduxFieldInputTypes) => boolean);
}

/**
 * Redux form textarea field component
 */
export const TextArea: React.FC<TextAreaProps> = ({
	showErrorsOn = 'touched',
	meta,
	input,
	theme = 'light',
	className,
	hint = '',
	...fieldProps
}) => {
	const id = `${meta.form}.${input.name}`;
	const showErrors = isFunction(showErrorsOn) ? showErrorsOn(meta, input) : meta.error && meta[showErrorsOn || ''];

	return (
		<div className={cn(css.textarea, css[theme], className, { [css.error]: showErrors })}>
			<TextAreaPure id={id} theme={theme} {...fieldProps} {...input} />
			<Errors show={showErrors}>{meta.error}</Errors>
			{hint && <Hint className={css.hint} text={hint} />}
		</div>
	);
};
